import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  productName?: string;
  category?: string;
}

interface Quiz {
  questions: QuizQuestion[];
  difficulty: string;
  generatedAt: string;
  productCount: number;
}

interface QuizResult {
  questionId: string;
  selectedIndex: number;
  isCorrect: boolean;
}

export function useProductTraining() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const generateQuiz = useCallback(async (options: {
    productId?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    questionCount?: number;
  } = {}) => {
    setIsGenerating(true);
    setQuiz(null);
    setResults([]);
    setCurrentIndex(0);
    setIsComplete(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase.functions.invoke('generate-product-quiz', {
        body: {
          productId: options.productId,
          difficulty: options.difficulty || 'medium',
          questionCount: options.questionCount || 5,
          tenantId: profile.tenant_id,
        },
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return null;
      }

      if (data?.success && data.quiz) {
        setQuiz(data.quiz);
        toast.success(`Quiz generated with ${data.quiz.questions.length} questions`);
        return data.quiz;
      }

      toast.error('Failed to generate quiz');
      return null;
    } catch (err: any) {
      console.error('Quiz generation error:', err);
      toast.error(err.message || 'Failed to generate quiz');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const answerQuestion = useCallback((selectedIndex: number) => {
    if (!quiz || currentIndex >= quiz.questions.length) return;

    const question = quiz.questions[currentIndex];
    const isCorrect = selectedIndex === question.correctIndex;

    setResults(prev => [...prev, {
      questionId: question.id,
      selectedIndex,
      isCorrect,
    }]);

    if (currentIndex + 1 >= quiz.questions.length) {
      setIsComplete(true);
    } else {
      setCurrentIndex(prev => prev + 1);
    }

    return isCorrect;
  }, [quiz, currentIndex]);

  const resetQuiz = useCallback(() => {
    setResults([]);
    setCurrentIndex(0);
    setIsComplete(false);
  }, []);

  const score = results.filter(r => r.isCorrect).length;
  const totalQuestions = quiz?.questions.length || 0;
  const scorePercentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  return {
    isGenerating,
    quiz,
    results,
    currentIndex,
    isComplete,
    currentQuestion: quiz?.questions[currentIndex] || null,
    score,
    totalQuestions,
    scorePercentage,
    generateQuiz,
    answerQuestion,
    resetQuiz,
  };
}
